<script setup>
// Eltafouk: close-disposition questions asked when an agent resolves a conversation.
// Button lists come from a one-day classification of 1,534 real conversations
// (2026-08-18), ordered by frequency, wording unified with the callcenter
// agent_disposition list so phone + chat reports aggregate under the same names.
//
// One question is on screen at a time: answering it retires the question to a
// one-line summary at the top and brings in the next. Nothing is lost — every
// answered line is a button back to its question.
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import { FOLLOW_UP_OUTCOME } from 'dashboard/helper/closeDisposition';

const emit = defineEmits(['submit']);

// Page-internal copy is Arabic-only by fork convention (kept as script data so the
// @intlify lint rule passes without an en.json round-trip).
const L = {
  TITLE: 'قبل ما تقفل المحادثة',
  DESCRIPTION: 'سؤال واحد في المرة — إجاباتك بتتجمع في تقرير الإغلاق.',
  REASON_LABEL: 'العميل كان عايز إيه؟',
  OUTCOME_LABEL: 'خلصت على إيه؟',
  CLASSIFICATION_LABEL: 'العميل ده إيه؟',
  CLASSIFICATION_HINT: 'بيتسأل مرة واحدة بس في حياة العميل',
  NOTE_PLACEHOLDER: 'اكتب السبب باختصار…',
  NEXT: 'التالي',
  EDIT: 'تعديل',
  OF: 'من',
  SNOOZE_LABEL: 'أرجّعهالك امتى؟',
  SNOOZE_HINT:
    'المحادثة مش هتتقفل — هترجع لوحدها في الميعاد ده، أو أول ما العميل يبعت.',
  CUSTOM_TIME_CONFIRM: 'اختار الوقت ده',
  NO_REPLY_HINT: 'مفيش أسئلة تانية — دوس «حل المحادثة».',
  READY_HINT: 'تمام — دوس «حل المحادثة».',
  FOLLOW_UP_READY: 'هترجعلك',
  CONFIRM: 'حل المحادثة',
  CONFIRM_FOLLOW_UP: 'أجّل المحادثة',
  CANCEL: 'إلغاء',
};

const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
const arabicDigits = n => String(n).replace(/\d/g, d => AR_DIGITS[Number(d)]);

const STEP_REASON = 'reason';
const STEP_OUTCOME = 'outcome';
const STEP_SNOOZE = 'snooze';
const STEP_CLASSIFICATION = 'classification';
const STEP_DONE = 'done';

// How long the follow-up is parked for. «بكرة» was the only option at first and the
// owner's objection was immediate: an hour is often the right answer. Presets cover
// the common cases and the last one opens a free date/time field.
const SNOOZE_CUSTOM = 'custom';
const SNOOZE_CHOICES = [
  { key: 'an_hour', label: 'بعد ساعة', hours: 1 },
  { key: 'three_hours', label: 'بعد ٣ ساعات', hours: 3 },
  { key: 'tomorrow', label: 'بكرة ٩ الصبح', tomorrow: true },
  { key: 'next_week', label: 'بعد أسبوع', days: 7, atNine: true },
  { key: SNOOZE_CUSTOM, label: 'وقت أحدده' },
];

const atNineAm = date => {
  const d = new Date(date);
  d.setHours(9, 0, 0, 0);
  return d;
};

const snoozeChoiceDate = choice => {
  const now = new Date();
  if (choice.hours) return new Date(now.getTime() + choice.hours * 3600 * 1000);
  if (choice.tomorrow) return atNineAm(now.getTime() + 86400000);
  if (choice.days) return atNineAm(now.getTime() + choice.days * 86400000);
  return null;
};

const formatSnooze = date =>
  new Intl.DateTimeFormat('ar-EG', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

const OTHER_REASON = 'حاجة تانية';
// The customer never said what they wanted — a tap on the welcome button, or a
// greeting, and then silence. There is no reason to record because he never gave
// one, and no way to classify a contact who wrote nothing: picking a guess would
// stamp `custom_customer_classification` on the ERP card permanently (the backend
// never overwrites an existing value). So this answer ENDS the form.
const NO_REPLY_REASON = 'العميل مش رد ولا كمل محادثة';

// Reasons clustered by what the customer was actually after, each cluster in its own
// colour so the eye lands on the right neighbourhood before reading a single word.
// Clusters AND the chips inside them are ordered by measured frequency (the 1,534
// classification): استفسار 681 · أوردر 337 · شحن وفلوس 150 · شكوى/شكر 57 · تانية 20.
const REASON_GROUPS = [
  {
    label: 'استفسار',
    tone: 'blue',
    items: [
      'سؤال عن كتاب',
      'سعر أو عرض',
      'هدايا المدرسين',
      'طلب نسخة أو عينة PDF',
    ],
  },
  {
    label: 'أوردر',
    tone: 'teal',
    items: ['أوردر جديد أو حجز', 'تأكيد أوردر أو تعديله'],
  },
  {
    label: 'شحن وفلوس',
    tone: 'amber',
    items: ['متابعة شحن', 'بريد ومندوب', 'تحصيل وفواتير'],
  },
  {
    label: 'شكوى أو شكر',
    tone: 'ruby',
    items: ['شكوى أو مرتجع', 'شكر وإشادة'],
  },
  { label: 'غير كده', tone: 'slate', items: [OTHER_REASON] },
];

const REASON_TONES = REASON_GROUPS.reduce((map, group) => {
  group.items.forEach(item => {
    map[item] = group.tone;
  });
  return map;
}, {});

const NO_REPLY_OUTCOME = 'العميل مرَدّش';
// Outcome colour follows the meaning, not a group: solved is green, needs-follow-up
// amber, unsolved red — so an agent skimming the report cards recognises the state
// by colour alone.
const OUTCOMES = [
  { label: 'اتحلت', tone: 'teal' },
  { label: FOLLOW_UP_OUTCOME, tone: 'amber' },
  { label: 'ماتحلتش', tone: 'ruby' },
  { label: NO_REPLY_OUTCOME, tone: 'slate' },
];

const OUTCOME_TONES = OUTCOMES.reduce((map, outcome) => {
  map[outcome.label] = outcome.tone;
  return map;
}, {});

const { t } = useI18n();

// Values are the ERP `custom_customer_classification` enum, verbatim; labels come
// from the same i18n set the conversation-header chip uses, so the word the agent
// taps here is the word they see on the header afterwards.
const CLASSIFICATIONS = [
  'STUDENT',
  'TEACHER',
  'LIBRARY',
  'CENTER',
  'OTHER',
].map(key => ({
  label: t(`CONVERSATION.HEADER.ERP_CLASSIFICATIONS.${key}`),
  value: key.charAt(0) + key.slice(1).toLowerCase(),
}));

// Full literal class strings — Tailwind's scanner reads source text, so a composed
// `bg-n-${tone}-9` would never make it into the build.
const TONES = {
  blue: {
    idle: 'border-n-blue-8 bg-n-blue-3 text-n-blue-12 hover:bg-n-blue-4',
    on: 'border-n-blue-9 bg-n-blue-9 text-white shadow-sm',
    label: 'text-n-blue-11',
  },
  teal: {
    idle: 'border-n-teal-8 bg-n-teal-3 text-n-teal-12 hover:bg-n-teal-4',
    on: 'border-n-teal-9 bg-n-teal-9 text-white shadow-sm',
    label: 'text-n-teal-11',
  },
  amber: {
    idle: 'border-n-amber-8 bg-n-amber-3 text-n-amber-12 hover:bg-n-amber-4',
    on: 'border-n-amber-9 bg-n-amber-9 text-white shadow-sm',
    label: 'text-n-amber-11',
  },
  ruby: {
    idle: 'border-n-ruby-8 bg-n-ruby-3 text-n-ruby-12 hover:bg-n-ruby-4',
    on: 'border-n-ruby-9 bg-n-ruby-9 text-white shadow-sm',
    label: 'text-n-ruby-11',
  },
  iris: {
    idle: 'border-n-iris-8 bg-n-iris-3 text-n-iris-12 hover:bg-n-iris-4',
    on: 'border-n-iris-9 bg-n-iris-9 text-white shadow-sm',
    label: 'text-n-iris-11',
  },
  slate: {
    idle: 'border-n-strong bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-1',
    on: 'border-n-slate-12 bg-n-slate-12 text-white shadow-sm',
    label: 'text-n-slate-11',
  },
};

const chipClass = (tone, selected) =>
  selected ? TONES[tone].on : TONES[tone].idle;

const dialogRef = ref(null);
const openState = ref(false);
const conversationContext = ref(null);
const needsClassification = ref(false);
const currentStep = ref(STEP_REASON);
const form = reactive({
  reason: '',
  note: '',
  outcome: '',
  classification: '',
  snoozeKey: '',
  snoozeAt: null, // Date
  customSnooze: '', // raw datetime-local value
});

const isNoReply = computed(() => form.reason === NO_REPLY_REASON);
const isOtherReason = computed(() => form.reason === OTHER_REASON);
// The button must say what it will actually do. «محتاجة متابعة» snoozes, so calling
// it «حل المحادثة» would be a straight lie about where the conversation goes next.
const isFollowUp = computed(() => form.outcome === FOLLOW_UP_OUTCOME);
const confirmLabel = computed(() =>
  isFollowUp.value ? L.CONFIRM_FOLLOW_UP : L.CONFIRM
);
const snoozeAtLabel = computed(() =>
  form.snoozeAt ? formatSnooze(form.snoozeAt) : ''
);
const readyHint = computed(() => {
  if (isNoReply.value) return L.NO_REPLY_HINT;
  if (isFollowUp.value) return `${L.FOLLOW_UP_READY} ${snoozeAtLabel.value}`;
  return L.READY_HINT;
});

const totalSteps = computed(
  () => 2 + (isFollowUp.value ? 1 : 0) + (needsClassification.value ? 1 : 0)
);
const stepNumber = computed(() => {
  if (currentStep.value === STEP_OUTCOME) return 2;
  if (currentStep.value === STEP_SNOOZE) return 3;
  if (currentStep.value === STEP_CLASSIFICATION) {
    return isFollowUp.value ? 4 : 3;
  }
  return 1;
});
const stepCounter = computed(
  () =>
    `${arabicDigits(stepNumber.value)} ${L.OF} ${arabicDigits(totalSteps.value)}`
);

// The retired questions. Each one is a button back to its own step, so
// "one question at a time" never means "you cannot fix what you already answered".
// The step being edited right now is excluded — it is already on screen below.
const answeredSteps = computed(() => {
  const rows = [];
  if (form.reason) {
    rows.push({
      step: STEP_REASON,
      label: L.REASON_LABEL,
      value: form.reason,
      tone: isNoReply.value ? 'slate' : REASON_TONES[form.reason] || 'slate',
    });
  }
  if (form.outcome && !isNoReply.value) {
    rows.push({
      step: STEP_OUTCOME,
      label: L.OUTCOME_LABEL,
      value: form.outcome,
      tone: OUTCOME_TONES[form.outcome] || 'slate',
    });
  }
  if (isFollowUp.value && form.snoozeAt) {
    rows.push({
      step: STEP_SNOOZE,
      label: L.SNOOZE_LABEL,
      value: snoozeAtLabel.value,
      tone: 'amber',
    });
  }
  if (form.classification) {
    const item = CLASSIFICATIONS.find(c => c.value === form.classification);
    rows.push({
      step: STEP_CLASSIFICATION,
      label: L.CLASSIFICATION_LABEL,
      value: item ? item.label : form.classification,
      tone: 'iris',
    });
  }
  return rows.filter(row => row.step !== currentStep.value);
});

const customSnoozeDate = computed(() => {
  if (!form.customSnooze) return null;
  const date = new Date(form.customSnooze);
  // A past instant would snooze into a conversation that reopens on the very next
  // sweep — accept only a real future moment.
  return Number.isNaN(date.getTime()) || date <= new Date() ? null : date;
});

const isComplete = computed(() => {
  if (!form.reason) return false;
  if (isNoReply.value) return true;
  if (!form.outcome) return false;
  if (isOtherReason.value && !form.note.trim()) return false;
  if (isFollowUp.value && !form.snoozeAt) return false;
  if (needsClassification.value && !form.classification) return false;
  return true;
});

// Picking a reason wipes whatever the previous reason had collected, so switching
// to «العميل مش رد ولا كمل محادثة» after answering the other questions cannot
// smuggle a stale outcome/classification through, and going back to change the
// reason leaves the later questions blank instead of pre-answered.
const clearSnooze = () => {
  form.snoozeKey = '';
  form.snoozeAt = null;
  form.customSnooze = '';
};

const selectReason = reason => {
  form.reason = reason;
  form.outcome = '';
  form.classification = '';
  form.note = '';
  clearSnooze();
  if (reason === NO_REPLY_REASON) {
    currentStep.value = STEP_DONE;
  } else if (reason !== OTHER_REASON) {
    // «حاجة تانية» needs its free-text line before we can move on, so it is the one
    // answer that keeps the question on screen and waits for «التالي».
    currentStep.value = STEP_OUTCOME;
  }
};

// Same reasoning one level down: changing the outcome re-opens the classification
// question blank rather than carrying the previous answer.
const stepAfterSnooze = () =>
  needsClassification.value ? STEP_CLASSIFICATION : STEP_DONE;

const selectOutcome = outcome => {
  form.outcome = outcome;
  form.classification = '';
  clearSnooze();
  // «محتاجة متابعة» asks WHEN before anything else: parking a conversation without
  // saying when it comes back is the same as losing it.
  if (outcome === FOLLOW_UP_OUTCOME) {
    currentStep.value = STEP_SNOOZE;
    return;
  }
  currentStep.value = stepAfterSnooze();
};

const selectSnooze = choice => {
  form.snoozeKey = choice.key;
  if (choice.key === SNOOZE_CUSTOM) {
    // Stay on the step: the free field needs a value and a confirm of its own.
    form.snoozeAt = null;
    return;
  }
  form.customSnooze = '';
  form.snoozeAt = snoozeChoiceDate(choice);
  currentStep.value = stepAfterSnooze();
};

const confirmCustomSnooze = () => {
  if (!customSnoozeDate.value) return;
  form.snoozeAt = customSnoozeDate.value;
  currentStep.value = stepAfterSnooze();
};

const selectClassification = value => {
  form.classification = value;
  currentStep.value = STEP_DONE;
};

const advanceFromNote = () => {
  if (!form.note.trim()) return;
  currentStep.value = STEP_OUTCOME;
};

const goToStep = step => {
  currentStep.value = step;
};

const close = () => {
  dialogRef.value?.close();
  conversationContext.value = null;
  openState.value = false;
};

const open = (context = null, { askClassification = false } = {}) => {
  openState.value = true;
  conversationContext.value = context;
  needsClassification.value = askClassification;
  currentStep.value = STEP_REASON;
  form.reason = '';
  form.note = '';
  form.outcome = '';
  form.classification = '';
  clearSnooze();
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (!isComplete.value) return;

  emit('submit', {
    reason: form.reason,
    note: form.note.trim() || null,
    // «مردّش» IS the outcome of a conversation the customer never continued —
    // stamping it keeps the outcome column of the report meaningful instead of
    // filling it with «بدون نتيجة» rows the agent was never asked about.
    outcome: isNoReply.value ? NO_REPLY_OUTCOME : form.outcome,
    classification:
      needsClassification.value && !isNoReply.value
        ? form.classification
        : null,
    // Unix seconds, the shape toggle_status expects. Only set for a follow-up —
    // every other answer resolves and must not carry a snooze time.
    snoozedUntil:
      isFollowUp.value && form.snoozeAt
        ? Math.floor(form.snoozeAt.getTime() / 1000)
        : null,
    context: conversationContext.value,
  });
  close();
};

const isOpen = () => openState.value;

defineExpose({ open, close, isOpen });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="L.TITLE"
    :description="L.DESCRIPTION"
    :confirm-button-label="confirmLabel"
    :cancel-button-label="L.CANCEL"
    :disable-confirm-button="!isComplete"
    @confirm="handleConfirm"
    @close="openState = false"
  >
    <div class="flex flex-col gap-2 min-h-[15rem]">
      <!-- Retired questions: one compact line each, and each line is the way back. -->
      <button
        v-for="row in answeredSteps"
        :key="row.step"
        type="button"
        class="flex items-center gap-3 px-3 py-2 transition-colors border rounded-lg border-n-weak bg-n-solid-2 hover:bg-n-alpha-1"
        @click="goToStep(row.step)"
      >
        <span class="text-xs text-n-slate-11">{{ row.label }}</span>
        <span
          class="px-2 py-0.5 text-xs font-medium border rounded-md"
          :class="TONES[row.tone].on"
        >
          {{ row.value }}
        </span>
        <span class="text-xs underline text-n-slate-10 ms-auto">
          {{ L.EDIT }}
        </span>
      </button>

      <!-- 🚨 سؤال واحد بس على الشاشة، و`mode="out-in"` عشان القديم يختفي الأول
           والجديد يدخل بعده — الاتنين مع بعض كانوا هيبانوا كأن الفورم بينط. -->
      <Transition name="step" mode="out-in">
        <section
          v-if="currentStep === 'reason'"
          key="reason"
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <header class="flex items-center gap-2">
            <span
              class="grid text-xs font-bold text-white rounded-full size-5 place-items-center bg-n-brand"
            >
              {{ arabicDigits(1) }}
            </span>
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ L.REASON_LABEL }}
            </h4>
            <span class="text-xs text-n-slate-10 ms-auto">
              {{ stepCounter }}
            </span>
          </header>

          <div
            v-for="group in REASON_GROUPS"
            :key="group.label"
            class="flex flex-col gap-1.5"
          >
            <span class="text-xs font-medium" :class="TONES[group.tone].label">
              {{ group.label }}
            </span>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="reason in group.items"
                :key="reason"
                type="button"
                class="px-3.5 py-2 text-sm border rounded-lg transition-colors"
                :class="chipClass(group.tone, form.reason === reason)"
                @click="selectReason(reason)"
              >
                {{ reason }}
              </button>
            </div>
          </div>

          <div v-if="isOtherReason" class="flex flex-col gap-2">
            <TextArea
              v-model="form.note"
              class="w-full"
              :placeholder="L.NOTE_PLACEHOLDER"
            />
            <Button
              :label="L.NEXT"
              color="blue"
              size="sm"
              type="button"
              class="self-start"
              :disabled="!form.note.trim()"
              @click="advanceFromNote"
            />
          </div>

          <!-- 🚨 مش شريحة وسط الشرايح: دي مش «حاجة العميل كان عايزها»، دي إعلان إن
               مفيش إجابة أصلاً — وهي كمان بتلغي باقي الأسئلة. فبتاخد صف لوحدها بعد
               فاصل، بإطار متقطع، عشان تتقري كنوع تاني من الإجابة مش كخيار زيادة. -->
          <div class="pt-3 border-t border-dashed border-n-weak">
            <button
              type="button"
              class="w-full px-3.5 py-2.5 text-sm font-medium border border-dashed rounded-lg transition-colors"
              :class="
                isNoReply
                  ? 'border-n-slate-12 bg-n-slate-12 text-white shadow-sm'
                  : 'border-n-strong bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
              "
              @click="selectReason(NO_REPLY_REASON)"
            >
              {{ NO_REPLY_REASON }}
            </button>
          </div>
        </section>

        <section
          v-else-if="currentStep === 'outcome'"
          key="outcome"
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <header class="flex items-center gap-2">
            <span
              class="grid text-xs font-bold text-white rounded-full size-5 place-items-center bg-n-brand"
            >
              {{ arabicDigits(2) }}
            </span>
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ L.OUTCOME_LABEL }}
            </h4>
            <span class="text-xs text-n-slate-10 ms-auto">
              {{ stepCounter }}
            </span>
          </header>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="outcome in OUTCOMES"
              :key="outcome.label"
              type="button"
              class="px-3.5 py-2 text-sm border rounded-lg transition-colors"
              :class="chipClass(outcome.tone, form.outcome === outcome.label)"
              @click="selectOutcome(outcome.label)"
            >
              {{ outcome.label }}
            </button>
          </div>
        </section>

        <section
          v-else-if="currentStep === 'snooze'"
          key="snooze"
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <header class="flex items-center gap-2">
            <span
              class="grid text-xs font-bold text-white rounded-full size-5 place-items-center bg-n-brand"
            >
              {{ arabicDigits(3) }}
            </span>
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ L.SNOOZE_LABEL }}
            </h4>
            <span class="text-xs text-n-slate-10 ms-auto">
              {{ stepCounter }}
            </span>
          </header>
          <span class="text-xs text-n-slate-11">{{ L.SNOOZE_HINT }}</span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="choice in SNOOZE_CHOICES"
              :key="choice.key"
              type="button"
              class="px-3.5 py-2 text-sm border rounded-lg transition-colors"
              :class="chipClass('amber', form.snoozeKey === choice.key)"
              @click="selectSnooze(choice)"
            >
              {{ choice.label }}
            </button>
          </div>
          <div
            v-if="form.snoozeKey === 'custom'"
            class="flex flex-wrap items-center gap-2"
          >
            <input
              v-model="form.customSnooze"
              type="datetime-local"
              class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-alpha-2 text-n-slate-12"
            />
            <Button
              :label="L.CUSTOM_TIME_CONFIRM"
              color="blue"
              size="sm"
              type="button"
              :disabled="!customSnoozeDate"
              @click="confirmCustomSnooze"
            />
          </div>
        </section>

        <section
          v-else-if="currentStep === 'classification'"
          key="classification"
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <header class="flex items-center gap-2">
            <span
              class="grid text-xs font-bold text-white rounded-full size-5 place-items-center bg-n-brand"
            >
              {{ arabicDigits(isFollowUp ? 4 : 3) }}
            </span>
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ L.CLASSIFICATION_LABEL }}
            </h4>
            <span class="text-xs text-n-slate-10 ms-auto">
              {{ stepCounter }}
            </span>
          </header>
          <span class="text-xs text-n-slate-11">
            {{ L.CLASSIFICATION_HINT }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="item in CLASSIFICATIONS"
              :key="item.value"
              type="button"
              class="px-3.5 py-2 text-sm border rounded-lg transition-colors"
              :class="chipClass('iris', form.classification === item.value)"
              @click="selectClassification(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </section>

        <p
          v-else
          key="done"
          class="px-3 py-2 text-sm font-medium border rounded-lg border-n-teal-8 bg-n-teal-3 text-n-teal-12"
        >
          {{ readyHint }}
        </p>
      </Transition>
    </div>

    <!-- 🚨 تذييل خاص بالنافذة دي بدل التذييل الافتراضي، لسبب واحد: التذييل
         الافتراضي في `Dialog.vue` بيحط `class="w-full"` على **الزرارين**
         و`justify-between` على الصف — وده مظبوط لنافذة تأكيد صغيرة، لكن النافذة
         دي `width="lg"` فبيطلع الزرارين كتلتين ضخمتين على طرفين متباعدين،
         والموظفة بتدوّر بعينها على زرار الحفظ.
         بنغيّره **هنا بس** — التذييل الافتراضي مستعمل في كل نوافذ التطبيق
         وتغييره مركزيًا يبقى تغيير مالوش علاقة بالبلاغ ده. -->
    <template #footer>
      <div class="flex items-center gap-3">
        <!-- 🚨 `type="button"`, NOT submit: this footer sits inside `Dialog.vue`'s
             `<form @submit.prevent="confirm">`, so a submit button fires BOTH its own
             @click AND the form submit → `@confirm` → handleConfirm runs twice and the
             whole request goes out twice. It was invisible while this only resolved
             (re-resolving an already-resolved conversation changes nothing), but the
             follow-up snooze writes a private note — and the first live test produced
             TWO identical notes and four «تم تأجيل المحادثة» activity lines.
             The Enter key still confirms: the form keeps its own hidden submit. -->
        <Button
          :label="confirmLabel"
          color="blue"
          type="button"
          :disabled="!isComplete"
          @click="handleConfirm"
        />
        <Button
          :label="L.CANCEL"
          variant="faded"
          color="slate"
          type="button"
          @click="close"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
/* Fast on purpose: the agent asked for the questions to follow each other quickly,
   so this is a nudge that the step changed, not an animation to sit through. */
.step-enter-active,
.step-leave-active {
  transition:
    opacity 0.13s ease,
    transform 0.13s ease;
}

.step-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.step-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (prefers-reduced-motion: reduce) {
  .step-enter-active,
  .step-leave-active {
    transition: none;
  }
}
</style>
